import { Container, Stack } from '@ethings/ui-toolkit';
import Box from '@mui/material/Box';
import { ContainerProps } from '@mui/material/Container';
import Drawer from '@mui/material/Drawer';
import { styled } from '@mui/system';
import { ComponentProps, FC, ReactNode, RefObject, type JSX } from 'react';
import { FlexBox } from '../../components/flex-box';
import { PageTitle } from '../../components/page-title';
import { SIDEBAR_FULL_WIDTH, SIDEBAR_WIDTH } from '../../constants';
import { useSidebarPinContext } from '../../hooks';

interface Props {
  actions?: ReactNode;
  clearAllFiltersButton: JSX.Element;
  containerSize?: ContainerProps['maxWidth'];
  content: ReactNode;
  dataExportComponent?: ReactNode;
  'data-testid'?: string;
  datePicker?: JSX.Element;
  extraActions?: JSX.Element;
  filterButton: JSX.Element;
  filterContent: JSX.Element;
  filters?: ReactNode;
  icon?: JSX.Element;
  pagination?: JSX.Element;
  scrollRef?: RefObject<HTMLDivElement | null>;
  search?: JSX.Element;
  setFilterOpen: (value: boolean) => void;
  showFilterButton?: boolean;
  showFilters?: boolean;
  subtitle?: ReactNode;
  title: ReactNode;
}

const StyledDrawer = styled(Drawer)({
  width: '350px',
  '& .MuiPaper-root': {
    width: 'inherit',
  },
});

const Row = styled((props: ComponentProps<typeof Stack>) => (
  <Stack direction="row" spacing={2} useFlexGap {...props} />
))({
  alignItems: 'center',
});

export const LayoutDesktop: FC<Props> = ({
  actions,
  clearAllFiltersButton,
  containerSize,
  content,
  dataExportComponent,
  datePicker,
  extraActions,
  filterButton,
  filterContent,
  filters,
  icon,
  pagination,
  scrollRef,
  search,
  setFilterOpen,
  showFilterButton,
  showFilters,
  subtitle,
  title,
  ...props
}) => {
  const { isPinned } = useSidebarPinContext();

  return (
    <FlexBox data-testid={props['data-testid']} id="list-page" className="ListPage-root">
      <Container maxWidth={containerSize} id="list-page-scroll" ref={scrollRef}>
        <StyledDrawer
          anchor="left"
          open={showFilters}
          onClose={() => setFilterOpen(false)}
          sx={{
            zIndex: 1300,
            '& .MuiPaper-root': {
              left: isPinned ? SIDEBAR_FULL_WIDTH : SIDEBAR_WIDTH,
            },
          }}
        >
          {filterContent}
        </StyledDrawer>
        <Stack direction="column" spacing={1} py={3} height={1}>
          <Row id="page-header" lineHeight="2rem" alignItems="center">
            <PageTitle data-testid="page-title" title={title} icon={icon} />
            <Box>
              {search}
            </Box>
            {(showFilterButton || dataExportComponent || extraActions) && (
              <Stack direction="row" spacing={1} alignItems='center'>
                {showFilterButton && filterButton}
                {dataExportComponent}
                {extraActions}
              </Stack>
            )}
          </Row>
          <Row justifyContent="end" flexWrap="wrap">
            <Box sx={{ my: 1, mr: 'auto' }}>{subtitle}</Box>
            <Stack direction="row" spacing={2} alignItems="start">{actions}</Stack>
          </Row>
          {filters
            && <Row flexWrap="wrap" mt="4px !important" spacing={0}>
              {clearAllFiltersButton}
              {filters}
            </Row>
          }
          <Stack id="content" spacing={2} py={1} flexGrow={1}>
            <Box flexGrow={1}>
              {content}
            </Box>
            <Box display="flex" sx={{ justifyContent: 'center', pb: 2 }}>
              {pagination}
            </Box>
          </Stack>
        </Stack>
      </Container>
      {datePicker}
    </FlexBox >
  );
};
